<template>
	<b-form ref="taskForm">
		<!-- Task Title -->
	<b-form-row class="mb-2">
			<b-col cols="12">
				<b-input-group size="sm">
					<template v-slot:prepend>
						<b-input-group-text>
							<b-icon-star
								v-if="localStar == 'N'"
								:id="'tasktablerow-icon-star-' + localTask.id"
								variant="secondary"
								shift-v="0"
								@click="localStar = 'Y'"
							/>

							<b-icon-star-fill
								v-if="localStar == 'Y'"
								:id="'tasktablerow-icon-star-' + localTask.id"
								variant="warning"
								@click="localStar = 'N'"
							/>
						</b-input-group-text>
					</template>

					<b-form-input
						id="title"
						data-testid="task-title"
						v-model="localTask.title"
						type="text"
						validated="true"
						:state="taskValidation.title"
						placeholder="The task you wish to track"
						size="sm"
					/>
				</b-input-group>
			</b-col>
		</b-form-row>

		<!-- Timeline & Duration Section -->
	<b-card class="detail-section-card mb-2" body-class="p-2">
	<div class="detail-section">
		<h6 class="detail-section-title mb-1">Timeline & Duration</h6>
			<b-row>
			
			<!-- Start Date/Time -->
			
			<b-col md="3">
				<label class="detail-label">Start Date</label>
				<b-form-datepicker
					id="startDate"
					data-testid="task-startDate"
					size="sm"
					v-model="startDateForSystem"
					validated="true"
					:state="taskValidation.startDate"
					:date-format-options="dateFormatOptions"
					:today-button="true"
					:reset-button="true"
					placeholder=""
				/>
			</b-col>
			<b-col md="2">
				<label class="detail-label">Start Time</label>
				<b-form-input
					id="startTime"
					data-testid="task-startTime"
					v-model="startTimeForSystem"
					type="time"
					validated="true"
					:state="taskValidation.startTime"
					size="sm"
				/>
			</b-col>

			<!-- Due Date/Time -->

			<b-col md="3">
				<label class="detail-label">Due Date</label>
				<b-form-datepicker
					id="endDate"
					data-testid="task-dueDate"
					size="sm"
					v-model="dueDateForSystem"
					validated="true"
					:state="taskValidation.endDate"
					:date-format-options="dateFormatOptions"
					:today-button="true"
					:reset-button="true"
					placeholder=""
				/>
			</b-col>
			<b-col md="2">
				<label class="detail-label">Due Time</label>
				<b-form-input
					id="endTime"
					data-testid="task-dueTime"
					v-model="dueTimeForSystem"
					type="time"
					validated="true"
					:state="taskValidation.endTime"
					size="sm"
				/>
			</b-col>
			<b-col md="2">
				<label class="detail-label">Length (min)</label>
				<b-form-input
					id="length"
					data-testid="task-length"
					size="sm"
					v-model.number="localTask.length"
					type="number"
					placeholder=""
				/>
			</b-col>
		</b-row>
		</div>
		</b-card>

		<!-- Repeat & Reminders Section -->
	<b-card class="detail-section-card mb-2" body-class="p-2">
	<div class="detail-section">
		<h6 class="detail-section-title mb-1">Repeat & Reminders</h6>
		<b-row>
			
			<!-- Repeat -->
			<b-col md="6">
				<label class="detail-label">Repeat</label>
				<b-form-select
					id="repeat"
					data-testid="task-repeat"
					v-model="localTask.repeat"
					validated="true"
					:state="taskValidation.repeat"
					:options="validRepeatFrequencies"
					value-field="item"
					text-field="name"
					size="sm"
				/>

				<b-input-group v-if="localTask.repeat == 'ADVANCED'" size="sm" class="mt-1">
					<b-form-input
						:id="'repeatFinal-' + localTask.id"
						v-model="repeatFinal"
						:lazy="true"
						placeholder="e.g. every 4 days, every 2nd monday"
						size="sm"
					/>
					<template v-slot:append>
						<b-button variant="outline-secondary" size="sm">
							<b-icon icon="question-circle" />
						</b-button>
					</template>
				</b-input-group>

				<!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Radio group for repeat from option -->
				<!-- WHY: Control when repeat calculates from (completed date or due date) -->
				<!-- UPDATED: 20260127 -- Using explicit :model-value/@update:modelValue for Vue 3 compatibility -->
				<b-form-group v-if="localTask.repeat != 'NONE'" class="mt-1 mb-0">
					<b-form-radio-group
						:model-value="localTask.repeatFrom"
						@update:modelValue="value => $set(localTask, 'repeatFrom', value)"
						name="repeatFrom"
						size="sm"
					>
						<b-form-radio value="completedDate">From Completed</b-form-radio>
						<b-form-radio value="due">From Due</b-form-radio>
					</b-form-radio-group>
				</b-form-group>
			</b-col>
			
			<!-- Remind -->
			<b-col md="6">
				<label class="detail-label">Reminder</label>
				<b-form-select
					id="remind"
					data-testid="task-remind"
					v-model="localTask.remind"
					:options="validReminderFrequencies"
					value-field="item"
					text-field="name"
					size="sm"
				/>
			</b-col>
		</b-row>
		</div>
		</b-card>

		<!-- Properties Section -->
	<b-card class="detail-section-card mb-2" body-class="p-2">
	<div class="detail-section">
		<h6 class="detail-section-title mb-1">Properties</h6>
		<b-row>
			
			<!-- Folder -->
			<b-col md="3">
				<label class="detail-label">Priority</label>
				<b-form-select
					id="priority"
					data-testid="task-priority"
					v-model="localTask.priority"
					validated="true"
					:state="taskValidation.priority"
					:options="validPriorities"
					value-field="item"
					text-field="name"
					size="sm"
				/>
			</b-col>

			<!-- Context -->
			<b-col md="3">
				<label class="detail-label">Status</label>
				<b-form-select
					id="status"
					data-testid="task-status"
					v-model="localTask.status"
					validated="true"
					:state="taskValidation.status"
					:options="validStatuses"
					value-field="item"
					text-field="name"
					size="sm"
				/>
			</b-col>
			<b-col md="3">
				<label class="detail-label">Folder</label>
				<b-form-select
					id="folder"
					data-testid="task-folder"
					v-model="localTask.folder"
					validated="true"
					:state="taskValidation.folder"
					:options="$store.getters.getFolders"
					size="sm"
				/>
			</b-col>
			<b-col md="3">
				<label class="detail-label">Context</label>
				<b-form-select
					id="context"
					data-testid="task-context"
					v-model="localTask.context"
					validated="true"
					:state="taskValidation.context"
					:options="$store.getters.getContexts"
					value-field="name"
					text-field="name"
					size="sm"
				/>
			</b-col>
		</b-row>
		</div>
		</b-card>

		<!-- Notes Section -->
		<b-card class="detail-section-card mb-3">
		<div class="detail-section">
			<h4 class="detail-section-title">Notes</h4>
		<b-form-row>
			
			<!-- Notes -->
			<b-col>
				<b-form-textarea
					id="notes"
					data-testid="task-notes"
					v-model="localTask.notes"
					rows="4"
					size="sm"
					placeholder="Supports Markdown."
				/>
			</b-col>
		</b-form-row>
		</div>
		</b-card>
		
		<!-- Action Buttons -->
		<!-- 20260126 -- Copilot (Claude Sonnet 4.5) -- Using -compat dropdown to avoid INSTANCE_LISTENERS error -->
	<div class="task-card-actions-footer mt-2">
			<b-button
				size="sm"
				variant="warning"
				class="cancel-button"
				@click="handleCancel"
			>
				Cancel
			</b-button>
			
			<!-- 20260203 -- Copilot (Claude Haiku 4.5) -- Add & Close option in dropdown -->
			<!-- WHY: Users need option to save and close modal immediately -->
			<b-dropdown
				variant="success"
				right
				split
				size="sm"
				class="action-dropdown"
				:text="primaryButtonText"
				@click="handleAdvancedAction(primaryAction)"
			>
				<b-dropdown-item @click="handleAdvancedAction(secondaryAction)">{{ secondaryButtonText }}</b-dropdown-item>
				<b-dropdown-divider></b-dropdown-divider>
				<b-dropdown-item @click="handleAdvancedAction('close')">Add & Close</b-dropdown-item>
			</b-dropdown>
		</div>
	</b-form>	
</template>

<script>

import genericOps from '@/mixins/genericOps'
import taskOps from '@/mixins/taskOps'

export default {
	name: 'TaskEntryForm',
	mixins: [taskOps, genericOps],
	props: {
		enableAdvancedControls: {
			type: Boolean,
			default: false
		},
		enableControls: {
			type: Boolean,
			default: false
		},
		task: {
               type: Object,
               default: function() {
				return Object.assign({}, this.emptyTask)
			},
          },
	},

	data() {
          return {
               dateFormatOptions: { year: 'numeric', month: 'numeric', day: '2-digit',  },
               hasError: false,
			previousAction: null,
			primaryAction: 'new',
			primaryButtonText: 'Add & Create New',
			resultMessage: '',
			secondaryAction: 'similar',
			secondaryButtonText: 'Add & Create Similar',
			tertiaryAction: 'close',
			taskValidation: {
				valid: true,
				title: null,
			},
          }
	},
	computed: {
          localStar: {
               get: function() {
                    if (
                         typeof this.localTask.star == 'undefined' ||
                         this.localTask.star === null ||
                         this.localTask.star == 'N'
                    ) {
                         return 'N'
                    } else {
                         return 'Y'
                    }
               },
               set: function(newVal) {
                    //this.localTask.star = newVal
                    this.$set(this.localTask, 'star', newVal)
               },
		},
		localTask: {
               get: function() {
                    return this.task
               },
               set: function(newVal) {
                    this.task = newVal
               },
          },
          parentTask: function() {
               return this.task.parentTask
          },
          parentTitle: function() {
               // 20260128 -- Copilot (Claude Sonnet 4.5) -- Added null check for parentTask.title
               // WHY: When creating child task, parentTask object may exist but be null/incomplete
               if (typeof this.parentTask != 'undefined' && this.parentTask !== null && this.parentTask.title) {
                    return this.parentTask.title
               } else {
                    return null
               }
          },
	},
	methods: {
		// 20260126 -- Copilot (Claude Sonnet 4.5) -- Made async to await saveTask2
		// WHY: Ensure task has _id from API before handling post-save actions
		// NOTE: saveTask2 already calls saveComplete and adds task to store, no need to emit
		async handleAction(action) {
			await this.saveTask2(this.taskValidation, this.localTask, false)

			if(this.taskValidation.valid) {
				// Notify parent of the action taken for modal control
				this.$emit('action:complete', action)
			} else {
				console.log('Invalid')
			}
		},
		// 20260126 -- Copilot (Claude Sonnet 4.5) -- Made async to await saveTask2
		// WHY: Ensure task has _id from API before handling post-save actions
		// NOTE: saveTask2 already calls saveComplete and adds task to store, no need to emit
		// 20260203 -- Copilot (Claude Haiku 4.5) -- Added 'close' action handling
		// WHY: Support 'Add & Close' option in dropdown
		async handleAdvancedAction(postAction) {
			
			console.log(postAction)

			// Only swap buttons for 'new' and 'similar' actions, not for 'close'
			if (postAction !== 'close') {
				// Current Action and Previous Action are different, update the buttons.
				if (
					(typeof this.previousAction != 'undefined' && this.previousAction !== null) ||
					this.previousAction != postAction
				) {
					if (postAction == 'new') {
						this.primaryButtonText = 'Add & Create New'
						this.primaryAction = postAction

						this.secondaryButtonText = 'Add & Create Similar'
						this.secondaryAction = 'similar'
					} else if (postAction == 'similar') {
						this.primaryButtonText = 'Add & Create Similar'
						this.primaryAction = 'similar'

						this.secondaryButtonText = 'Add & Create New'
						this.secondaryAction = 'new'
					}
				}
			}

			await this.saveTask2(this.taskValidation, this.localTask, false)

			if(this.taskValidation.valid) {
				// Notify parent of the action taken for modal control
				// 20260127 -- Copilot (Claude Sonnet 4.5) -- Parent (CreateTaskPopup) handles clearing fields
				// WHY: Centralize field clearing logic in parent component for better control
				this.$emit('action:complete', postAction)
			} else {
				console.log('Invalid')
			}
		},
		handleCancel() {
			this.$emit('cancelled:task')
		}
	}
}
</script>

<style scoped>
.detail-section-title {
	font-weight: 600;
	color: var(--text-primary);
	margin-bottom: var(--spacing-md);
	padding-bottom: var(--spacing-sm);
	border-bottom: 2px solid var(--accent-color);
	display: flex;
	align-items: center;
	gap: var(--spacing-xs);
	font-size: 0.8rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.detail-label {
	font-size: 0.625rem;
	font-weight: 500;
	color: var(--text-secondary);
	margin-bottom: var(--spacing-xs);
	text-transform: uppercase;
	letter-spacing: 0.05em;
	display: block;
}

.task-card-actions-footer {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 0.5rem;
	padding-top: 1rem;
	border-top: 1px solid #e9ecef;
	margin-top: 1rem;
}

.action-dropdown ::v-deep .btn-group {
	display: inline-flex;
}

.action-dropdown ::v-deep .btn {
	padding-left: 1rem;
	padding-right: 1rem;
	white-space: nowrap;
}

.action-dropdown {
	flex-shrink: 0;
	flex-grow: 0;
}

.cancel-button {
	flex-shrink: 0;
	flex-grow: 0;
}

.task-card-actions-footer {
	display: flex;
	gap: 0.5rem;
	padding-top: 0.5rem;
}

.task-card-actions-footer .btn-group {
	flex: 0 0 auto;
	min-width: 180px;
}

.task-card-actions-footer .dropdown-toggle {
	white-space: nowrap;
}

/* 20260127 -- Copilot (Claude Sonnet 4.5) -- Compact form styling for smaller displays */
/* WHY: Make form fit on 1050px height displays at 100% zoom */
.detail-section-card .card-body {
	padding: 0.5rem !important;
}

.detail-section-title {
	font-size: 0.9rem;
	font-weight: 600;
	margin-bottom: 0.5rem;
}

.detail-label {
	font-size: 0.85rem;
	margin-bottom: 0.25rem;
	display: block;
}

.form-row {
	margin-bottom: 0.5rem;
}
</style>
